/**
 * useAiPreview — AI Action Preview hook (Spec D).
 *
 * Listens for ai:preview_round events, manages countdown timer, sends
 * confirm/reject via WebSocket, and returns a canvas renderGhosts callback
 * for registration on the UI layer.
 */

import { useState, useEffect, useRef, useCallback, type Dispatch, type SetStateAction } from 'react';
import type { PendingTool } from '@roll1/shared';
import { useSessionContext } from '../contexts/SessionContext';
import { useTokenContext } from '../contexts/TokenContext';
import { useCanvasContext } from '../contexts/CanvasContext';

// ── AiPreviewState ─────────────────────────────────────────────

export interface AiPreviewState {
  conversationId: string;
  tools: PendingTool[];
  /** Total action count (batch_actions sub-actions expanded) */
  actionCount: number;
  /** Seconds remaining until auto-reject (120 → 0) */
  secondsLeft: number;
  /** Send confirm WS message and clear state */
  confirm: () => void;
  /** Send reject WS message and clear state */
  reject: () => void;
  /** Canvas render callback for UI layer ghost overlay */
  renderGhosts: () => void;
}

// ── Helpers ────────────────────────────────────────────────────

/** Count total actions, expanding batch_actions sub-actions */
export function countActions(tools: PendingTool[]): number {
  let count = 0;
  for (const tool of tools) {
    if (tool.name === 'batch_actions') {
      const actions = (tool.input['actions'] as PendingTool[] | undefined) ?? [];
      count += actions.length > 0 ? countActions(actions) : 1;
    } else {
      count += 1;
    }
  }
  return count;
}

const PREVIEW_TIMEOUT_S = 120;

// ── Hook ───────────────────────────────────────────────────────

export interface UseAiPreviewReturn {
  previewState: AiPreviewState | null;
  enablePreview: boolean;
  setEnablePreview: Dispatch<SetStateAction<boolean>>;
}

export function useAiPreview(): UseAiPreviewReturn {
  const { mp } = useSessionContext();
  const tokenCtx = useTokenContext();
  const canvasCtx = useCanvasContext();

  const [previewState, setPreviewState] = useState<Omit<AiPreviewState, 'secondsLeft' | 'confirm' | 'reject' | 'renderGhosts' | 'actionCount'> | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(PREVIEW_TIMEOUT_S);
  const [enablePreview, setEnablePreview] = useState(false);

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const conversationIdRef = useRef<string | null>(null);

  // Clear countdown timer
  const clearCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Start countdown timer
  const startCountdown = useCallback(() => {
    clearCountdown();
    setSecondsLeft(PREVIEW_TIMEOUT_S);
    countdownRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCountdown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearCountdown();
    };
  }, [clearCountdown]);

  // WS subscription
  useEffect(() => {
    const unsub = mp.onMessage((msg) => {
      if (msg.type === 'ai:preview_round') {
        const ev = msg as { type: 'ai:preview_round'; conversationId: string; tools: PendingTool[] };
        conversationIdRef.current = ev.conversationId;
        setPreviewState({ conversationId: ev.conversationId, tools: ev.tools });
        startCountdown();
      } else if (msg.type === 'ai:preview_timeout') {
        const ev = msg as { type: 'ai:preview_timeout'; conversationId: string };
        if (ev.conversationId === conversationIdRef.current) {
          clearCountdown();
          conversationIdRef.current = null;
          setPreviewState(null);
        }
      }
    });
    return unsub;
  }, [mp, startCountdown, clearCountdown]);

  // confirm handler
  const confirm = useCallback(() => {
    if (!conversationIdRef.current) return;
    mp.send({ type: 'ai:preview_confirm', conversationId: conversationIdRef.current });
    clearCountdown();
    conversationIdRef.current = null;
    setPreviewState(null);
  }, [mp, clearCountdown]);

  // reject handler
  const reject = useCallback(() => {
    if (!conversationIdRef.current) return;
    mp.send({ type: 'ai:preview_reject', conversationId: conversationIdRef.current });
    clearCountdown();
    conversationIdRef.current = null;
    setPreviewState(null);
  }, [mp, clearCountdown]);

  // renderGhosts callback (registered on UI layer)
  const toolsRef = useRef<PendingTool[]>([]);
  useEffect(() => {
    toolsRef.current = previewState?.tools ?? [];
  }, [previewState?.tools]);

  const renderGhosts = useCallback(() => {
    const canvas = canvasCtx.engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const camera = canvasCtx.engine.cameraRef.current;
    const dpr = canvasCtx.engine.dpr;
    const tools = toolsRef.current;
    if (tools.length === 0) return;

    // worldToScreen transform
    function worldToScreenX(wx: number): number {
      return (wx * camera.zoom + camera.offsetX) * dpr;
    }
    function worldToScreenY(wy: number): number {
      return (wy * camera.zoom + camera.offsetY) * dpr;
    }
    function worldToScreenSize(ws: number): number {
      return ws * camera.zoom * dpr;
    }

    ctx.save();
    renderToolsGhost(ctx, tools, tokenCtx.tokens.tokensRef.current, worldToScreenX, worldToScreenY, worldToScreenSize, camera.zoom * dpr);
    ctx.restore();
  }, [canvasCtx.engine, tokenCtx.tokens]);

  const builtPreviewState: AiPreviewState | null = previewState
    ? {
        conversationId: previewState.conversationId,
        tools: previewState.tools,
        actionCount: countActions(previewState.tools),
        secondsLeft,
        confirm,
        reject,
        renderGhosts,
      }
    : null;

  return { previewState: builtPreviewState, enablePreview, setEnablePreview };
}

// ── Ghost rendering helpers ────────────────────────────────────

type WorldToScreenFn = (v: number) => number;

function renderToolsGhost(
  ctx: CanvasRenderingContext2D,
  tools: PendingTool[],
  tokens: import('../types').Token[],
  worldToScreenX: WorldToScreenFn,
  worldToScreenY: WorldToScreenFn,
  worldToScreenSize: WorldToScreenFn,
  scale: number,
): void {
  for (const tool of tools) {
    switch (tool.name) {
      case 'token_move':
        renderTokenMoveGhost(ctx, tool, tokens, worldToScreenX, worldToScreenY, worldToScreenSize, scale);
        break;
      case 'create_token':
        renderCreateTokenGhost(ctx, tool, worldToScreenX, worldToScreenY, worldToScreenSize);
        break;
      case 'token_update':
        renderTokenUpdateGhost(ctx, tool, tokens, worldToScreenX, worldToScreenY, scale);
        break;
      case 'fog_reveal':
        renderFogRevealGhost(ctx, tool, worldToScreenX, worldToScreenY, worldToScreenSize);
        break;
      case 'batch_actions': {
        const subActions = (tool.input['actions'] as PendingTool[] | undefined) ?? [];
        if (subActions.length > 0) {
          renderToolsGhost(ctx, subActions, tokens, worldToScreenX, worldToScreenY, worldToScreenSize, scale);
        }
        break;
      }
      default:
        break;
    }
  }
}

function renderTokenMoveGhost(
  ctx: CanvasRenderingContext2D,
  tool: PendingTool,
  tokens: import('../types').Token[],
  worldToScreenX: WorldToScreenFn,
  worldToScreenY: WorldToScreenFn,
  worldToScreenSize: WorldToScreenFn,
  scale: number,
): void {
  const targetX = tool.input['x'] as number | undefined;
  const targetY = tool.input['y'] as number | undefined;
  if (targetX === undefined || targetY === undefined) return;

  const tokenId = tool.input['tokenId'] as string | undefined;
  const token = tokenId ? tokens.find(t => t.id === tokenId) : null;

  const sx = worldToScreenX(targetX);
  const sy = worldToScreenY(targetY);
  const size = worldToScreenSize(50); // default token size

  ctx.save();
  ctx.globalAlpha = 0.4;

  if (token?.imageEl) {
    ctx.drawImage(token.imageEl, sx - size / 2, sy - size / 2, size, size);
  } else if (token?.imageSrc) {
    const img = new Image();
    img.src = token.imageSrc;
    if (img.complete) {
      ctx.drawImage(img, sx - size / 2, sy - size / 2, size, size);
    } else {
      renderPlaceholderCircle(ctx, sx, sy, size / 2, '#888');
    }
  } else {
    renderPlaceholderCircle(ctx, sx, sy, size / 2, '#888');
  }

  // Draw arrow from current position if token found
  if (token) {
    const fromSx = worldToScreenX(token.x);
    const fromSy = worldToScreenY(token.y);
    ctx.globalAlpha = 0.6;
    ctx.setLineDash([6, 3]);
    ctx.strokeStyle = '#4CAF50';
    ctx.lineWidth = Math.max(1, scale);
    ctx.beginPath();
    ctx.moveTo(fromSx, fromSy);
    ctx.lineTo(sx, sy);
    ctx.stroke();
    ctx.setLineDash([]);
    // Arrowhead
    const angle = Math.atan2(sy - fromSy, sx - fromSx);
    const headLen = Math.max(8, scale * 4);
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(sx - headLen * Math.cos(angle - Math.PI / 6), sy - headLen * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(sx, sy);
    ctx.lineTo(sx - headLen * Math.cos(angle + Math.PI / 6), sy - headLen * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  }

  ctx.restore();
}

function renderCreateTokenGhost(
  ctx: CanvasRenderingContext2D,
  tool: PendingTool,
  worldToScreenX: WorldToScreenFn,
  worldToScreenY: WorldToScreenFn,
  worldToScreenSize: WorldToScreenFn,
): void {
  const x = tool.input['x'] as number | undefined;
  const y = tool.input['y'] as number | undefined;
  if (x === undefined || y === undefined) return;

  const name = (tool.input['name'] as string | undefined) ?? '';
  const sx = worldToScreenX(x);
  const sy = worldToScreenY(y);
  const size = worldToScreenSize(50);

  ctx.save();
  ctx.globalAlpha = 0.4;
  renderPlaceholderCircle(ctx, sx, sy, size / 2, '#9C27B0');
  if (name) {
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = '#fff';
    ctx.font = `${Math.max(10, size / 5)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(name, sx, sy - size / 2 - 4);
  }
  ctx.restore();
}

function renderTokenUpdateGhost(
  ctx: CanvasRenderingContext2D,
  tool: PendingTool,
  tokens: import('../types').Token[],
  worldToScreenX: WorldToScreenFn,
  worldToScreenY: WorldToScreenFn,
  scale: number,
): void {
  const tokenId = tool.input['tokenId'] as string | undefined;
  if (!tokenId) return;

  const token = tokens.find(t => t.id === tokenId);
  if (!token) return;

  const sx = worldToScreenX(token.x);
  const sy = worldToScreenY(token.y);

  ctx.save();
  ctx.font = `bold ${Math.max(10, scale * 12)}px sans-serif`;
  ctx.textAlign = 'center';

  const hp = tool.input['hp'] as number | undefined;
  const currentHp = tool.input['currentHp'] as number | undefined;
  const hpVal = hp ?? currentHp;
  if (hpVal !== undefined && typeof hpVal === 'number') {
    const delta = hpVal;
    ctx.fillStyle = delta >= 0 ? '#4CAF50' : '#F44336';
    ctx.fillText(`${delta >= 0 ? '+' : ''}${delta} HP`, sx, sy - 30 * scale);
  }

  ctx.restore();
}

function renderFogRevealGhost(
  ctx: CanvasRenderingContext2D,
  tool: PendingTool,
  worldToScreenX: WorldToScreenFn,
  worldToScreenY: WorldToScreenFn,
  worldToScreenSize: WorldToScreenFn,
): void {
  const x = tool.input['x'] as number | undefined;
  const y = tool.input['y'] as number | undefined;
  if (x === undefined || y === undefined) return;

  const sx = worldToScreenX(x);
  const sy = worldToScreenY(y);

  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = 'rgba(255, 220, 0, 0.8)';
  ctx.lineWidth = 2;

  const radius = tool.input['radius'] as number | undefined;
  const width = tool.input['width'] as number | undefined;
  const height = tool.input['height'] as number | undefined;

  if (radius !== undefined) {
    const sr = worldToScreenSize(radius);
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.stroke();
  } else if (width !== undefined && height !== undefined) {
    const sw = worldToScreenSize(width);
    const sh = worldToScreenSize(height);
    ctx.strokeRect(sx, sy, sw, sh);
  }

  ctx.setLineDash([]);
  ctx.restore();
}

function renderPlaceholderCircle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  color: string,
): void {
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}
